/**
 * Messages hooks (TanStack Query) — feedback on a message, a sub-resource of
 * its session upstream (…/sessions/{id}/messages/{message_id}/feedback).
 */

import { useMutation } from '@tanstack/react-query';
import i18n from '@/i18n';
import { sessionsApi } from '../api/endpoints/sessions';
import { errorToast } from '@/hooks/errorToast';
import { useAuthenticatedQuery } from '@/hooks/useAuthenticatedQuery';
import { useAuthStore } from '@/stores/auth';

/**
 * Query keys for messages
 */
export const messageKeys = {
  all: ['messages'] as const,
  feedback: (sessionId: string, messageId: string) => [...messageKeys.all, 'feedback', sessionId, messageId] as const,
};

/**
 * Submit feedback on a message.
 */
export function useMessageFeedback() {
  return useMutation({
    mutationFn: ({ sessionId, messageId, type }: { sessionId: string; messageId: string; type: string }) =>
      sessionsApi.createFeedback(sessionId, messageId, { type }),
    onError: () => {
      errorToast({
        context: 'messageFeedback',
        description: i18n.t('profile.feedback.failedDescription'),
      });
    },
  });
}

/**
 * The CALLER's own rating of one message, or null. Read lazily per message —
 * a message carries no rating upstream; the rating is the caller's record on
 * it, answered by the feedback list.
 */
export function useMyFeedback(sessionId: string, messageId: string): 'positive' | 'negative' | null {
  const meId = useAuthStore((s) => s.user?.id);
  const query = useAuthenticatedQuery({
    queryKey: messageKeys.feedback(sessionId, messageId),
    queryFn: () => sessionsApi.feedback(sessionId, messageId),
    enabled: !!sessionId && !!messageId && !!meId,
    staleTime: Infinity,
  });
  const mine = (query.data?.items ?? []).find((f) => f.author.performer === meId);
  if (!mine) return null;
  return mine.type === 'positive' ? 'positive' : 'negative';
}
